import { useTranslate } from "ra-core";
import { useMemo } from "react";

import {
  contactGender,
  translateContactGenderLabel,
} from "../contacts/contactModel";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";
import { toExcelDate, toLabels } from "./cells";
import { customFieldColumns } from "./customFieldColumns";
import type { SheetColumn } from "./types";

const emailOf = (contact: Contact, type: "Work" | "Home" | "Other") =>
  contact.email_jsonb?.find((email) => email.type === type)?.email;

const phoneOf = (contact: Contact, type: "Work" | "Home" | "Other") =>
  contact.phone_jsonb?.find((phone) => phone.type === type)?.number;

/** Spreadsheet columns of contacts, in the order of the export and the template. */
export const useContactColumns = (): SheetColumn<Contact>[] => {
  const translate = useTranslate();
  const { noteStatuses, customFields } = useConfigurationContext();

  return useMemo(() => {
    const label = (field: string) =>
      translate(`resources.contacts.fields.${field}`);
    const column = (key: string) => translate(`crm.spreadsheet.columns.${key}`);
    const genderOptions = contactGender.map((gender) => ({
      value: gender.value,
      label: translateContactGenderLabel(gender, translate),
    }));
    const columns: SheetColumn<Contact>[] = [
      {
        key: "first_name",
        header: label("first_name"),
        group: "identity",
        kind: "text",
        required: true,
        example: "Léa",
        get: (contact) => contact.first_name,
      },
      {
        key: "last_name",
        header: label("last_name"),
        group: "identity",
        kind: "text",
        example: "Martin",
        get: (contact) => contact.last_name,
      },
      {
        key: "title",
        header: label("title"),
        group: "identity",
        kind: "text",
        example: "Responsable RH",
        get: (contact) => contact.title,
      },
      {
        key: "gender",
        header: column("gender"),
        group: "identity",
        kind: "choice",
        options: genderOptions,
        get: (contact) => toLabels(contact.gender, genderOptions),
      },
      {
        key: "status",
        header: column("status"),
        group: "identity",
        kind: "choice",
        options: noteStatuses,
        example: noteStatuses[1]?.label,
        get: (contact) => toLabels(contact.status, noteStatuses),
      },
      {
        key: "lead_score",
        header: label("lead_score"),
        group: "identity",
        kind: "rating",
        example: 3,
        get: (contact) => contact.lead_score,
      },
      {
        key: "tags",
        header: column("tags"),
        group: "identity",
        kind: "text",
        hint: translate("crm.spreadsheet.hints.list"),
        get: (contact, lookups) =>
          contact.tags
            ?.map((id) => lookups.tags.get(id))
            .filter(Boolean)
            .join(", "),
      },
      {
        key: "company",
        header: label("main_company"),
        group: "contact",
        kind: "text",
        width: 28,
        example: "Banque Lémanique SA",
        hint: translate("crm.spreadsheet.hints.company"),
        get: (contact, lookups) =>
          contact.company_id != null
            ? lookups.companies.get(contact.company_id)
            : undefined,
      },
      {
        key: "other_companies",
        header: label("company_ids"),
        group: "contact",
        kind: "text",
        width: 28,
        hint: translate("crm.spreadsheet.hints.list"),
        get: (contact, lookups) =>
          contact.company_ids
            ?.filter((id) => id !== contact.company_id)
            .map((id) => lookups.companies.get(id))
            .filter(Boolean)
            .join(", "),
      },
      {
        key: "email_work",
        header: column("email_work"),
        group: "contact",
        kind: "text",
        width: 28,
        example: "lea.martin@banque-lemanique.ch",
        get: (contact) => emailOf(contact, "Work"),
      },
      {
        key: "email_home",
        header: column("email_home"),
        group: "contact",
        kind: "text",
        width: 28,
        get: (contact) => emailOf(contact, "Home"),
      },
      {
        key: "email_other",
        header: column("email_other"),
        group: "contact",
        kind: "text",
        width: 28,
        get: (contact) => emailOf(contact, "Other"),
      },
      {
        key: "phone_work",
        header: column("phone_work"),
        group: "contact",
        kind: "phone",
        example: "+41 22 123 45 68",
        get: (contact) => phoneOf(contact, "Work"),
      },
      {
        key: "phone_home",
        header: column("phone_home"),
        group: "contact",
        kind: "phone",
        get: (contact) => phoneOf(contact, "Home"),
      },
      {
        key: "phone_other",
        header: column("phone_other"),
        group: "contact",
        kind: "phone",
        get: (contact) => phoneOf(contact, "Other"),
      },
      {
        key: "linkedin_url",
        header: label("linkedin_url"),
        group: "contact",
        kind: "text",
        width: 28,
        get: (contact) => contact.linkedin_url,
      },
      {
        key: "has_newsletter",
        header: label("has_newsletter"),
        group: "contact",
        kind: "boolean",
        options: [
          { value: "true", label: translate("ra.boolean.true") },
          { value: "false", label: translate("ra.boolean.false") },
        ],
        get: (contact) =>
          contact.has_newsletter == null
            ? undefined
            : translate(
                contact.has_newsletter ? "ra.boolean.true" : "ra.boolean.false",
              ),
      },
      {
        key: "background",
        header: label("background"),
        group: "follow_up",
        kind: "long_text",
        width: 40,
        get: (contact) => contact.background,
      },
      {
        key: "first_seen",
        header: column("first_seen"),
        group: "follow_up",
        kind: "date",
        get: (contact) => toExcelDate(contact.first_seen),
      },
      {
        key: "last_seen",
        header: label("last_seen"),
        group: "follow_up",
        kind: "date",
        get: (contact) => toExcelDate(contact.last_seen),
      },
      {
        key: "sales_id",
        header: label("sales_id"),
        group: "follow_up",
        kind: "text",
        exportOnly: true,
        get: (contact, lookups) =>
          contact.sales_id != null
            ? lookups.sales.get(contact.sales_id)
            : undefined,
      },
      ...customFieldColumns<Contact>(customFields.contacts ?? [], {
        yes: translate("ra.boolean.true"),
        no: translate("ra.boolean.false"),
      }),
    ];
    return columns;
  }, [translate, noteStatuses, customFields]);
};
